import { detectRegion, detectStandaloneEnv, Region, StandaloneEnv } from "config/backend";
import { isAppBuild } from "utils/buildType";

// ── Library (SDK) configuration ──────────────────────────────────────────────

const SERVICE_NAME = "bim-webviewer-sdk";
const LIBRARY_NAME = "bim-webviewer-sdk";

/**
 * OpenTelemetry collector endpoints for each region
 * Source: https://procoretech.atlassian.net/wiki/spaces/CSE/pages/3300065546/Sending+Data+to+OpenTelemetry
 */
const OTEL_ENDPOINTS: Record<Region, string> = {
    [Region.STAGING]: "https://otlp.collector.ops.procoretech-qa.com/v1/traces",
    [Region.US01]: "https://otlp.collector.ops.na-east-01.procoretech.com/v1/traces",
    [Region.US02]: "https://otlp.collector.ops.na-west-02.procoretech.com/v1/traces",
    [Region.UK01]: "https://otlp.collector.ops.eu-west-02.procoretech.com/v1/traces",
    [Region.LOCAL]: "http://localhost:4318/v1/traces",
} as const;

/**
 * OpenTelemetry metrics collector endpoints for each region
 */
const OTEL_METRICS_ENDPOINTS: Record<Region, string> = {
    [Region.STAGING]: "https://otlp.collector.ops.procoretech-qa.com/v1/metrics",
    [Region.US01]: "https://otlp.collector.ops.na-east-01.procoretech.com/v1/metrics",
    [Region.US02]: "https://otlp.collector.ops.na-west-02.procoretech.com/v1/metrics",
    [Region.UK01]: "https://otlp.collector.ops.eu-west-02.procoretech.com/v1/metrics",
    [Region.LOCAL]: "http://localhost:4318/v1/metrics",
} as const;

export const SPAN_NAMES = {
    VIEWER_LOAD: "bim-webviewer-sdk.viewer.load",
    AUTH: "bim-webviewer-sdk.auth",
    ERROR_RECOVERY: "bim-webviewer-sdk.error.recovery",
    VIEWPOINT_LOADED: "bim-webviewer-sdk.viewpoint.loaded",
} as const;

// ── Standalone (app build) configuration ─────────────────────────────────────

const STANDALONE_SERVICE_NAME = "novoweb";
const STANDALONE_LIBRARY_NAME = "novoweb";

/**
 * Standalone OTEL trace collector endpoints (Novorender Azure infrastructure)
 */
const STANDALONE_OTEL_ENDPOINTS: Record<StandaloneEnv, string> = {
    [StandaloneEnv.TEST]: "https://otel-collector-web-test.azurewebsites.net/v1/traces",
    [StandaloneEnv.PRODUCTION]: "https://otel-collector-web-prod.azurewebsites.net/v1/traces",
    [StandaloneEnv.LOCAL]: "http://localhost:4318/v1/traces",
} as const;

/**
 * Standalone OTEL metrics collector endpoints (Novorender Azure infrastructure)
 */
const STANDALONE_OTEL_METRICS_ENDPOINTS: Record<StandaloneEnv, string> = {
    [StandaloneEnv.TEST]: "https://otel-collector-web-test.azurewebsites.net/v1/metrics",
    [StandaloneEnv.PRODUCTION]: "https://otel-collector-web-prod.azurewebsites.net/v1/metrics",
    [StandaloneEnv.LOCAL]: "http://localhost:4318/v1/metrics",
} as const;

const STANDALONE_SPAN_NAMES = {
    VIEWER_LOAD: "novoweb.viewer.load",
    AUTH: "novoweb.auth",
    ERROR_RECOVERY: "novoweb.error.recovery",
    VIEWPOINT_LOADED: "novoweb.viewpoint.loaded",
} as const;

// ── Resolved config ──────────────────────────────────────────────────────────

export type SpanNameMap = typeof SPAN_NAMES | typeof STANDALONE_SPAN_NAMES;

export interface ResolvedTelemetryConfig {
    endpoint: string;
    metricsEndpoint: string;
    serviceName: string;
    libraryName: string;
    region: Region | StandaloneEnv;
    version: string;
    spanNames: SpanNameMap;
}

/**
 * Resolve telemetry configuration based on build type and environment.
 * Returns `null` when telemetry should be disabled (localhost).
 */
export function resolveTelemetryConfig(): ResolvedTelemetryConfig | null {
    const isStandalone = isAppBuild();

    if (isStandalone) {
        const env = detectStandaloneEnv();

        if (env === StandaloneEnv.LOCAL) {
            return null;
        }

        return {
            endpoint: STANDALONE_OTEL_ENDPOINTS[env],
            metricsEndpoint: STANDALONE_OTEL_METRICS_ENDPOINTS[env],
            serviceName: STANDALONE_SERVICE_NAME,
            libraryName: STANDALONE_LIBRARY_NAME,
            region: env,
            version: __APP_VERSION__,
            spanNames: STANDALONE_SPAN_NAMES,
        };
    }

    const region = detectRegion();

    if (region === Region.LOCAL) {
        return null;
    }

    return {
        endpoint: OTEL_ENDPOINTS[region],
        metricsEndpoint: OTEL_METRICS_ENDPOINTS[region],
        serviceName: SERVICE_NAME,
        libraryName: LIBRARY_NAME,
        region,
        version: __LIB_VERSION__,
        spanNames: SPAN_NAMES,
    };
}
